import {meta} from "./meta";

export const home = {
    meta: meta,
    hero:
        {
            withSubscribe: {
                title: "Stop Email Spoofing Before It Hurts",
                subTitle: "AI-powered DMARC protection in minutes — no DNS skills needed.",
                form: {
                    input: {
                        placeholder: "joe@example.com"
                    },
                    button: {
                        text: "Sign Up"
                    },
                    checkBox: {
                        consent: "I agree my data is collected to get in touch for product updates.",
                        feedback: "Please agree to data collection"
                    },
                    success: "Successfully Registered",
                    error: "Error registering. Please try again",
                },
                dashboardImage: {
                    alt: "DMARC monitoring dashboard shows suspicious activities"
                }
            },
            withAnalyze: {
                title: "Stop Email Spoofing Before It Hurts",
                subTitle: "AI-powered DMARC protection in minutes — no DNS skills needed.",
                ctaInfo: "Analyze Your Domain's Security Identify email problems and implement necessary fixes without expert knowledge and risk.",
                form: {
                    domain: {
                        placeholder: "Type your domain (e.g. example.com)"
                    },
                    selector: {
                        placeholder: "DKIM Selector (optional)"
                    },
                    button: {
                        text: "Scan Domain"
                    },
                    checkBox: {
                        consent: "I agree my data is collected to get in touch for product updates.",
                        feedback: "Please agree to data collection"
                    },
                    success: "Successfully Registered",
                    error: "Error registering. Please try again",
                },
                dashboardImage: {
                    alt: "DMARC monitoring dashboard shows suspicious activities"
                }
            },
        },
    pageSections: {
        checker: {
            title: "Check Your DMARC Setup",
            sub: "Instant diagnosis of your DNS configuration",
            description: "Instantly check your domain’s DMARC, SPF, and DKIM configuration for common errors and vulnerabilities.",
            cta: "Is Your Domain Affected?",
            imageAlt: "DMARC record checker tool interface",
            list: {
                one: "✅ Instantly analyze your DNS records",
                two: "✅ Identify common SPF/DKIM/DMARC issues",
                three: "✅ No login or account required"
            }
        },
        generator: {
            title: "Generate a Valid DMARC Record",
            sub: "Create policy records with guided best practices",
            description: "Use our free generator to craft a fully valid and compliant DMARC record—ready to deploy into your DNS.",
            cta: "Generate Record",
            imageAlt: "DMARC record generator example screenshot",
            list: {
                one: "🛠️ Create policy records with guided logic",
                two: "📄 Copy-paste ready output for your DNS",
                three: "🔒 Compliant with RFCs and best practices"
            }
        },
        enterprise: {
            title: "Enterprise Email Protection",
            sub: "Scalable DMARC Automation for Organizations",
            description: "Unlock robust email security with cross-domain control, AI-powered insights, and compliance-ready dashboards.",
            cta: "Explore Enterprise Solutions",
            imageAlt: "Enterprise DMARC dashboard overview",
            list: {
                one: "🏢 Designed for large infrastructures",
                two: "📊 Cross-domain and group policy management",
                three: "📈 AI-based threat pattern recognition"
            }
        }
    },
    footer: {
        vendorText: "DMARC Flow is a product by"
    },
    advantages: {
        title: "How DMARC Protects Your Brand — Effortlessly with DMARCFlow",
        subTitle: "Prevent phishing & spoofing in minutes. Visualize threats, stay compliant, and gain full email control.",
        1: {
            title: "Protection",
            description: "Protect your email domain from unauthorized access and minimize risks such as spam, fraud, or phishing attacks.",
            icon: "🔒"
        },
        2: {
            title: "Transparency",
            description: "Gain detailed insights into who is sending messages on behalf of your email domain and maintain full control.",
            icon: "👁️"
        },
        3: {
            title: "Optimized Delivery",
            description: "Leverage innovative technologies to ensure your emails are reliably delivered and accepted by recipients.",
            icon: "📬"
        },
        4: {
            title: "Trustworthiness",
            description: "Ensure that your emails are easily recognized by recipients and perceived as legitimate.",
            icon: "👤"
        }
    },
    contact: {
        title: "Get in Touch Now!",
        subTitle: "Have questions? Get expert help — no strings attached.",
        buttonCta: "Schedule an Appointment"
    },
    features: {
        1: {
            title: "DMARC Reporting",
            description: "Visualizes DMARC data to detect unauthorized use of your domains.",
            buttonLabel: "Learn More",
            icon: "📊",
            link: "/dmarc-reporting"
        },
        2: {
            title: "Real-Time Monitoring",
            description: "Continuously monitor your email domains for threats and unauthorized activities.",
            buttonLabel: "Learn More",
            icon: "⏱️",
            link: "/realtime-monitoring"
        },
        3: {
            title: "AI-Powered",
            description: "An innovative service that completely relieves you during setup and builds trust with recipients.",
            buttonLabel: "Learn More",
            icon: "🤖",
            link: "/ai-based-dmarc-reporting"
        }
    },
    latestBlog: {
        title: "Latest from Our Blog",
        1: {
            title: "5 Tips for Better Email Security",
            alt: "Checklist for secure email configuration",
            description: "Discover how you can improve email security and domain protection with simple steps.",
            buttonLabel: "Get Security Tips",
            link: "/five-tips-for-email-security",
            img: {
                alt: "Computer showing email client in a cafe"
            }
        },
        2: {
            title: "DMARC Made Easy",
            description: "An introduction to DMARC and why it is essential for your email security.",
            buttonLabel: "Read DMARC Introduction",
            link: "/dmarc-made-easy",
            img: {
                alt: "Computer showing dashboard"
            }
        },
        3: {
            title: "Why BIMI Is Important for Your Brand",
            description: "Learn how BIMI can help build trust with your email recipients.",
            buttonLabel: "More about BIMI",
            link: "/why-bimi-is-important-for-your-brand",
            img: {
                alt: "ID card"
            }
        }
    },
    "followUs": {
        "title": "Follow us on Social Media",
        "description": "Stay up to date with the latest news, product updates, and email security tips. Follow us on our social media channels for exclusive content and real-time insights on DMARC, SPF, and DKIM.",
        "aria": "Follow us on {{platform}}"
    },
    faq: {
        "title": "Frequently Asked Questions about DMARCFlow",
        "items": [
            {
                "question": "What is DMARC and why is it important?",
                "answer": "DMARC (Domain-based Message Authentication, Reporting & Conformance) protects your domain from spoofing and phishing. It ensures that only authorized senders can send emails on behalf of your domain and provides detailed reports about abuse."
            },
            {
                "question": "How does SPF work in DMARCFlow?",
                "answer": "SPF (Sender Policy Framework) defines which servers are allowed to send emails on behalf of your domain. DMARCFlow centralizes SPF configuration, simplifies setup, and helps avoid the 10 DNS lookup limit."
            },
            {
                "question": "What is DKIM and how does DMARCFlow support it?",
                "answer": "DKIM (DomainKeys Identified Mail) adds a digital signature to your emails. DMARCFlow helps you create and manage DKIM keys for multiple domains and ensures all emails are properly signed."
            },
            {
                "question": "What happens if an email fails DMARC?",
                "answer": "DMARCFlow analyzes DMARC reports and provides recommendations. If an email fails DMARC, the policy you choose ('none', 'quarantine', or 'reject') will determine how it's handled."
            },
            {
                "question": "How does DMARCFlow support BIMI setup?",
                "answer": "BIMI (Brand Indicators for Message Identification) allows your logo to appear in inboxes. DMARCFlow validates the necessary records and simplifies the BIMI onboarding process."
            },
            {
                "question": "What reporting features does DMARCFlow offer?",
                "answer": "DMARCFlow provides daily and weekly reports, visualizes threat attempts, and translates raw XML data into actionable insights."
            },
            {
                "question": "Is DMARCFlow GDPR compliant?",
                "answer": "Yes. DMARCFlow does not store personal data and processes reporting data in accordance with GDPR. All data is stored exclusively in the EU."
            },
            {
                "question": "How long does it take to set up DMARCFlow?",
                "answer": "Initial setup takes less than 10 minutes. A guided wizard helps you configure SPF, DKIM, and DMARC including testing and validation."
            },
            {
                "question": "What makes DMARCFlow different from other DMARC tools?",
                "answer": "DMARCFlow combines user-friendliness with technical depth. It supports multiple domains, role management, simple configuration, and clear visual reports."
            },
            {
                "question": "How can I try DMARCFlow?",
                "answer": "We offer personal plans for DMARCFlow. Just sign up, add the DNS records, and reports will start appearing within 24 hours."
            }
        ]
    }
};